// src/modules/app-views/invoices/pages/Document/tabs/RecordTab/ConfigCard.jsx
// *******************************************************************************
// INVOICE CONFIGURATION CARD
// *******************************************************************************
// Card for fiscal document configuration (NFe/NFCe).
// Contains all required fiscal fields with dynamic behavior based on doc type.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useCallback, useEffect, useMemo } from 'react';
import { Card, Row, Col, Divider, Form, Collapse, Alert, Typography } from 'antd';
import { SettingOutlined, SettingFilled, LinkOutlined } from '@ant-design/icons';
import SimpleSelect from 'components/SimpleComponents/SimpleSelect';
import SimpleSegment from 'components/SimpleComponents/SimpleSegment';
import SimpleDatePicker from 'components/SimpleComponents/SimpleDatePicker';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';
import translateX from 'utils/translateX';
import useZustand from 'hooks/useZustand';
import {
  OPERATION_TYPE,
  CONSUMER_PRESENCE,
  INVOICE_PURPOSE,
  FREIGHT_MODALITY,
  EMISSION_ENVIRONMENT,
  FINAL_CONSUMER,
  INTERMEDIATION,
  INVOICE_NATURE,
} from 'services/fiscalService';
import SimpleInput from 'components/SimpleComponents/SimpleInput';
import appService from 'services/appService';

const { Text } = Typography;

// *******************************************************************************
// CONSTANTS
// *******************************************************************************

const DOCUMENT_TYPE_OPTIONS = [
  { value: 'nfe', label: 'NF-e' },
  { value: 'nfce', label: 'NFC-e' },
];

const OPERATION_TYPE_OPTIONS = [
  { value: OPERATION_TYPE.EXIT, labelKey: 'operation_exit' },
  { value: OPERATION_TYPE.ENTRY, labelKey: 'operation_entry' },
];

const FINAL_CONSUMER_OPTIONS = [
  { value: FINAL_CONSUMER.YES, labelKey: 'yes' },
  { value: FINAL_CONSUMER.NO, labelKey: 'no' },
];

const ENVIRONMENT_OPTIONS = [
  { value: EMISSION_ENVIRONMENT.PRODUCTION, labelKey: 'production' },
  { value: EMISSION_ENVIRONMENT.HOMOLOGATION, labelKey: 'homologation' },
];

const CONSUMER_PRESENCE_OPTIONS = [
  { value: CONSUMER_PRESENCE.IN_PERSON, labelKey: 'presence_in_person' },
  { value: CONSUMER_PRESENCE.INTERNET, labelKey: 'presence_internet' },
  { value: CONSUMER_PRESENCE.TELEMARKETING, labelKey: 'presence_telemarketing' },
  { value: CONSUMER_PRESENCE.DELIVERY_NFCE, labelKey: 'presence_delivery' },
  { value: CONSUMER_PRESENCE.IN_PERSON_OUTSIDE, labelKey: 'presence_outside' },
  { value: CONSUMER_PRESENCE.OTHER, labelKey: 'presence_other' },
];

const INVOICE_PURPOSE_OPTIONS = [
  { value: INVOICE_PURPOSE.NORMAL, labelKey: 'purpose_normal' },
  { value: INVOICE_PURPOSE.COMPLEMENTARY, labelKey: 'purpose_complementary' },
  { value: INVOICE_PURPOSE.ADJUSTMENT, labelKey: 'purpose_adjustment' },
  { value: INVOICE_PURPOSE.RETURN, labelKey: 'purpose_return' },
];

const NATURE_OF_OPERATION_OPTIONS = [
  { value: INVOICE_NATURE.SALE, labelKey: 'nature_sale' },
  { value: INVOICE_NATURE.RETURN, labelKey: 'nature_return' },
  { value: INVOICE_NATURE.TRANSFER, labelKey: 'nature_transfer' },
  { value: INVOICE_NATURE.BONUS, labelKey: 'nature_bonus' },
  { value: INVOICE_NATURE.DEMO, labelKey: 'nature_demo' },
  { value: INVOICE_NATURE.CONSIGNMENT, labelKey: 'nature_consignment' },
  { value: INVOICE_NATURE.OTHER, labelKey: 'nature_other' },
];

const INTERMEDIATION_OPTIONS = [
  { value: INTERMEDIATION.OWN_OPERATION, labelKey: 'intermediation_own' },
  { value: INTERMEDIATION.VIA_INTERMEDIARY, labelKey: 'intermediation_marketplace' },
];

const RESOURCE_NFE = 'moduleresources_nfe';
const RESOURCE_NFCE = 'moduleresources_nfce';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ConfigCard = ({ data, viewMode, issuer }) => {
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  const { form } = useSimpleFormContext();
  const watermarkStore = useZustand('theme', 'setWatermark');
  const storedSubscription = useZustand('auth', 'subscription');
  const storedCompany = useZustand('auth', 'company');

  // 2. HOOKS & REFS
  const storedLanguage = useZustand('theme', 'language');

  // 3. LOCAL VARIABLES I
  const isLocked = viewMode === 'view';
  const isAuthorized = data?.status === 'authorized';
  const isIssuerActive = issuer?.fiscalData?.isActive === true;
  const issuerName = issuer?.razaoSocial || issuer?.nomeFantasia || translateX('issuer');
  const isNewDocument = !data?._id || data._id === 'add';

  // Only show document type selector when BOTH resources are available
  const subscriptionResources = storedSubscription?.resources || [];
  const hasNfeResource = subscriptionResources.includes(RESOURCE_NFE);
  const hasNfceResource = subscriptionResources.includes(RESOURCE_NFCE);
  const showDocumentTypeSelector = hasNfeResource && hasNfceResource;

  const formDocumentType = Form.useWatch('documentType', form);

  // Effective document type: existing docs use data.documentType; new docs use form value or subscription default
  const effectiveDocumentType = isNewDocument
    ? formDocumentType || (hasNfceResource && !hasNfeResource ? 'nfce' : 'nfe')
    : data?.documentType || 'nfe';
  const isNfce = effectiveDocumentType === 'nfce';
  const isNfe = effectiveDocumentType === 'nfe';

  const purpose = Form.useWatch('purpose', form);
  const isComplementaryOrReturn =
    purpose === INVOICE_PURPOSE.COMPLEMENTARY ||
    purpose === INVOICE_PURPOSE.RETURN ||
    purpose === INVOICE_PURPOSE.ADJUSTMENT;

  const documentTypeOptions = useMemo(
    () => DOCUMENT_TYPE_OPTIONS.map(opt => ({ value: opt.value, label: opt.label })),
    []
  );

  const operationTypeOptions = useMemo(
    () => OPERATION_TYPE_OPTIONS.map(opt => ({ value: opt.value, label: translateX(opt.labelKey) })),
    [storedLanguage]
  );

  const finalConsumerOptions = useMemo(
    () => FINAL_CONSUMER_OPTIONS.map(opt => ({ value: opt.value, label: translateX(opt.labelKey) })),
    [storedLanguage]
  );

  const environmentOptions = useMemo(
    () => ENVIRONMENT_OPTIONS.map(opt => ({ value: opt.value, label: translateX(opt.labelKey) })),
    [storedLanguage]
  );

  // 4. LOCAL STATE

  // 5. EFFECTS
  useEffect(() => {
    return () => {
      watermarkStore({ content: translateX('') });
    };
  }, []);

  // Auto-correct operationType for existing NFC-e documents loaded with wrong value
  // NFC-e (model 65) only allows exit operations per Brazilian legislation
  useEffect(() => {
    if (!form || !isNfce || isNewDocument) return;

    const currentOperationType = form.getFieldValue('operationType');
    if (currentOperationType === OPERATION_TYPE.ENTRY) {
      form.setFieldsValue({ operationType: OPERATION_TYPE.EXIT });
    }
  }, [form, isNfce, isNewDocument]);

  // Set environment based on document type and issuer fiscal configuration
  useEffect(() => {
    if (!form || !issuer) return;

    const isNewDocument = !data?._id || data._id === 'add';

    // For existing documents: preserve saved environment, only update watermark
    if (!isNewDocument && data?.environment) {
      const formEnvironment = form.getFieldValue('environment') ?? data.environment;
      watermarkStore({
        content: translateX(formEnvironment === 'homologation' ? 'homologation' : ''),
      });
      return;
    }

    // For new documents: derive environment from issuer/company fiscal config.
    // Only override if company explicitly configured 'production' - schema defaults to
    // 'homologation' which should not override initialValues (Document/index sets 'production').
    const issuerFiscalConfig = isNfce ? issuer.fiscalData?.nfce : issuer.fiscalData?.nfe;
    const companyFiscalConfig = isNfce ? storedCompany?.fiscalData?.nfce : storedCompany?.fiscalData?.nfe;
    const fiscalConfig = issuerFiscalConfig ?? companyFiscalConfig;

    if (fiscalConfig?.environment === 'production') {
      form.setFieldsValue({ environment: 'production' });
    }

    const formEnvironment = form.getFieldValue('environment');
    watermarkStore({
      content: translateX(formEnvironment === 'homologation' ? 'homologation' : ''),
    });
  }, [form, effectiveDocumentType, isNfce, issuer, storedCompany, watermarkStore, data?._id, data?.environment]);

  // 6. HANDLERS
  const handleEnvironment = value => {
    if (value === 'production' && issuer) {
      const issuerFiscalConfig = isNfce ? issuer.fiscalData?.nfce : issuer.fiscalData?.nfe;
      const companyFiscalConfig = isNfce ? storedCompany?.fiscalData?.nfce : storedCompany?.fiscalData?.nfe;
      const configuredEnvironment = issuerFiscalConfig?.environment ?? companyFiscalConfig?.environment;

      if (configuredEnvironment !== 'production') {
        const docTypeName = isNfce ? 'NFC-e' : 'NF-e';
        const issuerNameLocal = issuer.razaoSocial || issuer.nomeFantasia || translateX('issuer');

        appService.notification({
          flag: 'warning',
          title: translateX('action_not_allowed'),
          description: translateX('issuer_not_configured_for_production', {
            issuerName: issuerNameLocal,
            docType: docTypeName,
          }),
        });

        if (form) {
          form.setFieldsValue({ environment: 'homologation' });
        }
        return;
      }
    }

    watermarkStore({ content: translateX(value === 'homologation' ? 'homologation' : '') });
  };

  const handleDocumentTypeChange = useCallback(
    value => {
      if (!form) return;

      if (issuer) {
        const isNfeEnabled = issuer.fiscalData?.nfe?.isEnabled === true;
        const isNfceEnabled = issuer.fiscalData?.nfce?.isEnabled === true;

        if (value === 'nfce' && !isNfceEnabled) {
          appService.notification({
            flag: 'warning',
            title: translateX('document_type_not_enabled', { docType: 'NFC-e' }),
            description: translateX('document_type_not_enabled_description', {
              issuerName,
              docType: 'NFC-e',
            }),
          });
          if (isNfeEnabled) {
            form.setFieldsValue({ documentType: 'nfe' });
          }
          return;
        }

        if (value === 'nfe' && !isNfeEnabled) {
          appService.notification({
            flag: 'warning',
            title: translateX('document_type_not_enabled', { docType: 'NF-e' }),
            description: translateX('document_type_not_enabled_description', {
              issuerName,
              docType: 'NF-e',
            }),
          });
          if (isNfceEnabled) {
            form.setFieldsValue({ documentType: 'nfce' });
          }
          return;
        }
      }

      const isNewDoc = !data?._id || data._id === 'add';
      const fiscalConfig = value === 'nfce' ? issuer?.fiscalData?.nfce : issuer?.fiscalData?.nfe;
      const configuredEnvironment = fiscalConfig?.environment || 'homologation';

      if (value === 'nfce') {
        form.setFieldsValue({
          operationType: OPERATION_TYPE.EXIT,
          finalConsumer: FINAL_CONSUMER.YES,
          freightModality: FREIGHT_MODALITY.NO_FREIGHT,
          purpose: INVOICE_PURPOSE.NORMAL,
          consumerPresence: CONSUMER_PRESENCE.IN_PERSON,
          natureOfOperation: INVOICE_NATURE.SALE,
          ...(isNewDoc && { environment: configuredEnvironment }),
        });
      } else {
        form.setFieldsValue({
          operationType: OPERATION_TYPE.EXIT,
          finalConsumer: FINAL_CONSUMER.YES,
          freightModality: FREIGHT_MODALITY.NO_FREIGHT,
          purpose: INVOICE_PURPOSE.NORMAL,
          consumerPresence: CONSUMER_PRESENCE.IN_PERSON,
          ...(isNewDoc && { environment: configuredEnvironment }),
        });
      }

      if (isNewDoc) {
        watermarkStore({
          content: translateX(configuredEnvironment === 'homologation' ? 'homologation' : ''),
        });
      }
    },
    [form, issuer, issuerName, data?._id, watermarkStore]
  );

  // 7. LOCAL VARIABLES II

  // 8. UI
  if (!isIssuerActive) {
    return (
      <Alert
        type="error"
        showIcon
        title={translateX('issuer_not_enabled_for_invoice_emission')}
        description={translateX('issuer_not_enabled_for_invoice_emission_description', {
          issuerName,
        })}
        className="mb-4"
      />
    );
  }

  return (
    <>
      {/* Row 1: Segments - Document Type | Operation | Final Consumer | Environment */}
      {(showDocumentTypeSelector || !isNfce) && (
        <Row gutter={[16, 16]}>
          {showDocumentTypeSelector && (
            <Col xs={24} sm={12} md={6}>
              <SimpleSegment
                name="documentType"
                label="document_type"
                options={documentTypeOptions}
                disabled={isLocked || isAuthorized}
                onChange={handleDocumentTypeChange}
              />
            </Col>
          )}

          {!isNfce && (
            <>
              <Col xs={24} sm={12} md={showDocumentTypeSelector ? 6 : 8}>
                <SimpleSegment
                  name="operationType"
                  label="operation_type"
                  options={operationTypeOptions}
                  disabled={isLocked || isAuthorized}
                />
              </Col>

              <Col xs={24} sm={12} md={showDocumentTypeSelector ? 6 : 8}>
                <SimpleSegment
                  name="finalConsumer"
                  label="final_consumer"
                  options={finalConsumerOptions}
                  disabled={isLocked || isAuthorized}
                />
              </Col>

              <Col xs={24} sm={12} md={showDocumentTypeSelector ? 6 : 8}>
                <SimpleSegment
                  name="environment"
                  label="environment"
                  onChange={handleEnvironment}
                  options={environmentOptions}
                  disabled={isLocked || isAuthorized}
                />
              </Col>
            </>
          )}
        </Row>
      )}

      {/* Row 2: Nature of Operation | Purpose - hidden for NFCe */}
      {!isNfce && (
        <>
          <Divider style={{ margin: '16px 0' }} />
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={12}>
              <SimpleSelect
                name="natureOfOperation"
                label="nature_of_operation"
                options={NATURE_OF_OPERATION_OPTIONS}
                disabled={isLocked || isAuthorized || isComplementaryOrReturn}
                showSearch
                allowClear={false}
              />
            </Col>

            <Col xs={24} sm={12} md={12}>
              <SimpleSelect
                name="purpose"
                label="invoice_purpose"
                options={INVOICE_PURPOSE_OPTIONS}
                disabled={isLocked || isAuthorized}
              />
            </Col>
          </Row>
        </>
      )}

      <Row gutter={[16, 16]} style={{ paddingTop: 8 }}>
        <Col xs={24} sm={8}>
          <SimpleDatePicker
            name="issueDate"
            label="issue_date"
            showTime
            format="DD/MM/YYYY"
            disabled={isLocked || isAuthorized}
          />
        </Col>

        <Col xs={24} sm={8}>
          <SimpleSelect
            name="intermediation"
            label="intermediation"
            options={INTERMEDIATION_OPTIONS}
            disabled={isLocked || isAuthorized}
          />
        </Col>

        <Col xs={24} sm={8}>
          <SimpleSelect
            name="consumerPresence"
            label="consumer_presence"
            options={CONSUMER_PRESENCE_OPTIONS}
            disabled={isLocked || isAuthorized}
          />
        </Col>
      </Row>

      {/* Reference NFe Field - Only for NFe complementary/return/adjustment */}
      {isNfe && isComplementaryOrReturn && (
        <>
          <Divider style={{ margin: '16px 0' }} />
          <Row gutter={[16, 16]}>
            {!['authorized', 'canceled'].includes(data?.status) && (
              <Col xs={24}>
                <Alert
                  type="info"
                  showIcon
                  icon={<LinkOutlined />}
                  title={translateX(
                    purpose === INVOICE_PURPOSE.COMPLEMENTARY
                      ? 'complementary_nfe_reference'
                      : purpose === INVOICE_PURPOSE.ADJUSTMENT
                        ? 'adjustment_nfe_reference'
                        : 'return_nfe_reference'
                  )}
                  description={translateX(
                    purpose === INVOICE_PURPOSE.COMPLEMENTARY
                      ? 'complementary_nfe_reference_description'
                      : purpose === INVOICE_PURPOSE.ADJUSTMENT
                        ? 'adjustment_nfe_reference_description'
                        : 'return_nfe_reference_description'
                  )}
                  style={{ marginBottom: 12 }}
                />
              </Col>
            )}
            <Col xs={24} md={16}>
              <SimpleInput
                name={['referenceNfe', 'accessKey']}
                label="reference_access_key"
                placeholder="reference_access_key_placeholder"
                maxLength={44}
                disabled={isLocked || isAuthorized}
                rules={[
                  { required: true, message: translateX('required') },
                  { len: 44, message: translateX('access_key_must_be_44_digits') },
                  { pattern: /^\d{44}$/, message: translateX('access_key_only_numbers') },
                ]}
              />
            </Col>
            <Col xs={24} md={8}>
              <SimpleInput
                name={['referenceNfe', 'documentNumber']}
                label="reference_document_number"
                disabled={isLocked || isAuthorized}
                tooltip="reference_document_number_tooltip"
              />
            </Col>
          </Row>
        </>
      )}

      <Row>
        <Col span={24}>
          <SimpleInput name="note" style={{ width: '100%' }} />
        </Col>
      </Row>
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ConfigCard;
